import { useUser } from '@clerk/clerk-react';
import type { MemberPublicMetadata, MemberRole } from '../lib/types';

export interface MemberContext {
  storeId: string;
  memberId: string;
  role: MemberRole;
  active: boolean;
  username: string | null;
}

/**
 * The device's own identity, read from Clerk `publicMetadata` — the same four
 * values the API reads from the JWT claims.
 *
 * Used client-side only, and only for the endpoints that take a store or member
 * id in the *path* (`/quota/:storeId`, `/stores/:storeId/members`). It is never
 * sent as a body or query field on the endpoints that derive scope from the
 * token: the API would reject that, and rightly.
 *
 * Returns `null` while Clerk is still loading or when signed out. It also
 * returns `null` when the metadata is missing entirely, which is what a
 * self-signed-up account looks like — such an account cannot pass `verifyAuth`
 * anyway, so treating it as unauthenticated here matches the server.
 */
export function useMember(): MemberContext | null {
  const { isLoaded, isSignedIn, user } = useUser();

  if (!isLoaded || !isSignedIn || !user) return null;

  const metadata = user.publicMetadata as Partial<MemberPublicMetadata> | undefined;
  if (!metadata?.store_id || !metadata.member_id || !metadata.role) return null;

  return {
    storeId: metadata.store_id,
    memberId: metadata.member_id,
    role: metadata.role,
    active: metadata.active !== false,
    username: user.username,
  };
}
